import { type FC, createContext, useContext, useMemo } from 'react'

import { Root as RadixTabsRoot } from '@radix-ui/react-tabs'

import type { TabsProps, TabsSize, TabsVariant } from './types'

/**
 * Tabs context to share variant and size with sub-components
 */
export const TabsContext = createContext<{ inTab: boolean; variant: TabsVariant; size: TabsSize }>({
  variant: 'border',
  size: 'md',
  inTab: false,
})

export const useTabsContext = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('useTabsContext must be used within a Tabs component')
  }
  return context
}

/**
 * Tabs root component - Accessible tab system with variant and size support
 *
 * Uses Radix UI for accessibility and Compound Pattern for flexible composition.
 *
 * @param {string} defaultValue - Default active tab value (must match a Tabs.Cell)
 * @param {TabsVariant} variant - Tab style: 'border' (bottom border) or 'box' (colored background) - default: 'border'
 * @param {TabsSize} size - Tab size: 'lg', 'md' or 'sm' - default: 'md'
 *
 * @example
 * // Tabs with border variant and badges
 * <Tabs defaultValue="all" variant="border" size="md">
 *   <Tabs.Cells>
 *     <Tabs.Cell value="all">
 *       <Tabs.TextCell>All</Tabs.TextCell>
 *       <Tabs.Badge type="neutral">12</Tabs.Badge>
 *     </Tabs.Cell>
 *     <Tabs.Cell value="active">In Progress</Tabs.Cell>
 *   </Tabs.Cells>
 *   <Tabs.Content value="all">All content</Tabs.Content>
 *   <Tabs.Content value="active">Active content</Tabs.Content>
 * </Tabs>
 */
export const TabsRoot: FC<TabsProps> = ({
  defaultValue,
  value,
  onChangeValue,
  variant = 'border',
  size = 'md',
  children,
}) => {
  const contextValue = useMemo(() => ({ inTab: true, variant, size }), [variant, size])

  return (
    <TabsContext.Provider value={contextValue}>
      <RadixTabsRoot
        defaultValue={defaultValue}
        value={value}
        onValueChange={onChangeValue}
        className="tabs-root"
      >
        {children}
      </RadixTabsRoot>
    </TabsContext.Provider>
  )
}
